import { CircleCloseIcon, UploadedDocIcon } from 'assets/icons';
import { LoadingSpinnerAnimation } from 'assets/animations';
import { FC, useCallback, useEffect, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { twMerge } from 'tailwind-merge';
import Lottie from 'lottie-react';
import If from 'components/If/If';
import { UploadAttachmentFile } from 'types/baseTypes';
import { useAppDispatch } from 'hooks';
import { NotifierTypes } from 'constants/notifierConstants';
import { showNotification } from 'utils/Notifier';
import { FILE_TYPE, FILE_UPLOAD_SIZE } from 'constants/common';
import { useGetPresignedUrlsMutation } from 'containers/invoices/api';
import { PresignedUrlAttachment } from 'containers/invoices/invoices.model';
import { FileUploadType } from 'containers/disputes/disputes.model';
import { CompressionLibraries, handleImageCompression } from 'components/file-upload/compression';
import { uploadFileToPresignedUrl } from 'services/presignedUpload';
import AttachmentPreview from 'components/file-preview/AttachmentPreview';

interface MultiFileUploadProps {
  className?: string;
  setFiles: React.Dispatch<React.SetStateAction<UploadAttachmentFile[]>>;
  id: string;
  height?: string;
  width?: string;
  subText?: string;
  maxFileSize?: number;
  FileUploadIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string }> | null;
  files: UploadAttachmentFile[];
  maxFileSizeErrorMessage?: string;
  invalidFileTypeErrorMessage?: string;
  allowMultiple?: boolean;
  onFileRemove: (fileId: number | string) => void;
  teamId: string;
  useCase: string;
  showFileList?: boolean;
  allowImageCompression?: boolean;
  supportedFileTypes?: FileUploadType;
  maxFiles?: number;
  isUploading: boolean;
  setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
  uploadToPresigned?: boolean;
  uploadAbortSignal?: AbortSignal;
  previewClassName?: string;
  enableFilePreview?: boolean;
  allowPaste?: boolean;
}

const MultiFileUpload: FC<MultiFileUploadProps> = ({
  className = '',
  setFiles,
  id,
  height = 'h-[100px]',
  width = 'w-full',
  subText = 'PNG, JPG, or WEBP format files up to 8MB',
  maxFileSize = FILE_UPLOAD_SIZE.SIZE_8_MB_BYTES,
  FileUploadIcon,
  files,
  maxFileSizeErrorMessage = 'File size should not exceed 8 MB',
  invalidFileTypeErrorMessage = 'Invalid file type. Please select a valid file.',
  allowMultiple = true,
  onFileRemove,
  teamId,
  useCase,
  showFileList = true,
  allowImageCompression = true,
  supportedFileTypes,
  maxFiles,
  isUploading,
  setIsUploading,
  uploadToPresigned = false,
  uploadAbortSignal,
  previewClassName = '',
  enableFilePreview = false,
  allowPaste = false
}) => {
  const dispatch = useAppDispatch();
  const [getPresignedUrls] = useGetPresignedUrlsMutation();
  const [previewState, setPreviewState] = useState<{ isOpen: boolean; currentIndex: number }>({
    isOpen: false,
    currentIndex: 0
  });

  const getFilePreviewUrl = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result) {
          resolve(reader.result as string);
        } else {
          reject(new Error('Failed to read file'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const compressFileIfNeeded = async (file: File): Promise<File> => {
    if (!allowImageCompression) return file;

    const isImage = file.type.startsWith('image/');
    if (!isImage) return file;

    if (file.size <= FILE_UPLOAD_SIZE.SIZE_1_MB) return file;

    try {
      if (file.type === FILE_TYPE.PNG) {
        return await handleImageCompression({
          library: CompressionLibraries.COMPRESSOR_JS,
          file: file
        });
      }
      return await handleImageCompression({
        library: CompressionLibraries.BROWSER_IMAGE_COMPRESSION,
        file: file
      });
    } catch (error) {
      return file;
    }
  };

  const isMaxFilesReached = maxFiles ? files?.length >= maxFiles : false;
  const remainingFileSlots = maxFiles ? Math.max(0, maxFiles - files?.length) : undefined;

  const handleDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (maxFiles && files.length >= maxFiles) {
        // when maxFiles is reached
        showNotification(dispatch, NotifierTypes.INFO, `Maximum ${maxFiles} files allowed.`, 4000, 'max-files-reached');
        return;
      }

      if (rejectedFiles?.[0]?.file.size > maxFileSize) {
        showNotification(dispatch, NotifierTypes.INFO, maxFileSizeErrorMessage, 4000, 'file-size-error');
        return;
      }

      if (rejectedFiles?.[0]?.errors?.[0].code === 'file-invalid-type') {
        showNotification(dispatch, NotifierTypes.INFO, invalidFileTypeErrorMessage, 4000, 'invalid-file-type');
        return;
      }

      if (rejectedFiles?.[0]?.errors?.[0].code === 'too-many-files') {
        // when too many files added at once
        showNotification(dispatch, NotifierTypes.INFO, `Maximum ${maxFiles} files allowed.`, 4000, 'too-many-files');
        return;
      }

      if (acceptedFiles.length === 0) return;

      setIsUploading(true);

      try {
        const processedFiles = await Promise.all(acceptedFiles?.map(file => compressFileIfNeeded(file)));

        if (!uploadToPresigned) {
          // In deferred mode, just create previews and store files without uploading to S3
          const filePromises = processedFiles.map(async (file, index) => {
            const previewData = await getFilePreviewUrl(file);
            const tempId = Date.now() + index;
            return {
              file,
              previewData,
              tempId
            };
          });

          const filesWithPreviews = await Promise.all(filePromises);

          const attachmentFiles: UploadAttachmentFile[] = filesWithPreviews.map(({ file, previewData, tempId }) => ({
            tempId,
            title: file.name.replace(/\s+/g, '-'),
            description: '',
            url: '', // Will be set after upload
            urlLink: '',
            getUrlLink: '',
            previewData,
            fileType: file.type,
            rawFile: file // Store File object
          }));

          setFiles(prev => [...prev, ...attachmentFiles] as UploadAttachmentFile[]);
        } else {
          // upload to S3 right away
          const attachments: PresignedUrlAttachment[] = processedFiles?.map(file => ({
            filename: file.name.replace(/\s+/g, '-'),
            fileType: file.type
          }));

          const presignedUrlsResponse = await getPresignedUrls({
            teamId,
            case: useCase,
            attachments
          }).unwrap();

          const uploadPromises = processedFiles?.map(async (file, index) => {
            try {
              const presignedUrlItem = presignedUrlsResponse.data[index];
              if (!presignedUrlItem) {
                return { success: false };
              }

              const uploadSuccess = await uploadFileToPresignedUrl(
                file,
                presignedUrlItem.presignedURL,
                uploadAbortSignal
              );
              if (!uploadSuccess) {
                return { success: false };
              }

              const previewData = await getFilePreviewUrl(file);

              const uploadedFile = {
                tempId: `${Date.now()}-${index}`,
                title: file.name.replace(/\s+/g, '-'),
                description: '',
                url: presignedUrlItem.path,
                urlLink: presignedUrlItem.presignedURL,
                getUrlLink: presignedUrlItem.getPresignedURL,
                previewData,
                fileType: file.type
              };

              setFiles(prev => [...prev, uploadedFile as UploadAttachmentFile]);
              return { success: true };
            } catch (error) {
              if (error instanceof DOMException && error.name === 'AbortError') {
                throw error;
              }
              return { success: false };
            }
          });

          const uploadResults = await Promise.all(uploadPromises);
          const hasFailedUploads = uploadResults.some(result => result && !result.success);

          if (hasFailedUploads) {
            showNotification(
              dispatch,
              NotifierTypes.ERROR,
              'Failed to upload some files. Please try again.',
              4000,
              'file-upload-failed'
            );
          }
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        showNotification(
          dispatch,
          NotifierTypes.ERROR,
          'Failed to upload files. Please try again.',
          4000,
          'upload-failed'
        );
      } finally {
        setIsUploading(false);
      }
    },
    [
      teamId,
      useCase,
      maxFileSize,
      maxFileSizeErrorMessage,
      invalidFileTypeErrorMessage,
      maxFiles,
      files.length,
      getPresignedUrls,
      setFiles,
      dispatch,
      allowImageCompression,
      setIsUploading,
      uploadToPresigned,
      uploadAbortSignal
    ]
  );

  const validateFile = (file: File): { isValid: boolean; error?: string } => {
    // Check file size
    if (file.size > maxFileSize) {
      return { isValid: false, error: 'file-size' };
    }
    // Check file type if supportedFileTypes is specified
    if (supportedFileTypes) {
      const fileType = file.type;
      const supportedMimeTypes = Object.keys(supportedFileTypes);

      const isValidType = supportedMimeTypes.some(acceptedType => {
        if (acceptedType.endsWith('/*')) {
          const typePrefix = acceptedType.split('/')[0];
          return fileType.startsWith(`${typePrefix}/`);
        }
        return fileType === acceptedType;
      });
      if (!isValidType) {
        return { isValid: false, error: 'invalid-type' };
      }
    }
    return { isValid: true };
  };

  const handlePaste = useCallback(
    async (e: ClipboardEvent) => {
      if (isMaxFilesReached) {
        showNotification(dispatch, NotifierTypes.INFO, `Maximum ${maxFiles} files allowed.`, 4000, 'max-files-reached');
        return;
      }
      const items = e.clipboardData?.items;
      if (!items) return;
      const pastedFiles: File[] = [];
      const rejectedFiles: { file: File; error: string }[] = [];
      // Extract files from clipboard
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            const validation = validateFile(file);
            if (validation.isValid) {
              pastedFiles.push(file);
            } else {
              rejectedFiles.push({ file, error: validation.error || 'unknown' });
            }
          }
        }
      }
      // Handle rejected files
      if (rejectedFiles.length > 0) {
        const firstRejection = rejectedFiles[0];
        if (firstRejection.error === 'file-size') {
          showNotification(dispatch, NotifierTypes.INFO, maxFileSizeErrorMessage, 4000, 'file-size-error');
          return;
        }
        if (firstRejection.error === 'invalid-type') {
          showNotification(dispatch, NotifierTypes.INFO, invalidFileTypeErrorMessage, 4000, 'invalid-file-type');
          return;
        }
      }
      // Check if pasting would exceed max files
      if (maxFiles && files.length + pastedFiles.length > maxFiles) {
        showNotification(dispatch, NotifierTypes.INFO, `Maximum ${maxFiles} files allowed.`, 4000, 'max-files-reached');
        return;
      }
      // Check if multiple files are pasted but multiple is not allowed
      if (!allowMultiple && pastedFiles.length > 1) {
        showNotification(dispatch, NotifierTypes.INFO, 'Only one file is allowed.', 4000, 'single-file-only');
        return;
      }
      if (pastedFiles.length > 0) {
        // Use the existing handleDrop function to process files
        await handleDrop(pastedFiles, []);
      }
    },
    [
      isMaxFilesReached,
      isUploading,
      maxFiles,
      files.length,
      allowMultiple,
      maxFileSize,
      supportedFileTypes,
      maxFileSizeErrorMessage,
      invalidFileTypeErrorMessage,
      dispatch,
      handleDrop
    ]
  );

  useEffect(() => {
    if (!allowPaste) return;
    const handlePasteEvent = (e: ClipboardEvent) => {
      handlePaste(e);
    };
    document.addEventListener('paste', handlePasteEvent);
    return () => {
      document.removeEventListener('paste', handlePasteEvent);
    };
  }, [handlePaste, allowPaste]);

  const {
    getRootProps,
    getInputProps,
    open: openFileDialog
  } = useDropzone({
    onDrop: handleDrop,
    accept: supportedFileTypes ? { ...supportedFileTypes } : undefined,
    maxSize: maxFileSize,
    multiple: allowMultiple,
    maxFiles: remainingFileSlots,
    noClick: isMaxFilesReached,
    noKeyboard: isMaxFilesReached
  });

  const handleFileClick = (index: number) => {
    if (!enableFilePreview) return;
    setPreviewState({ isOpen: true, currentIndex: index });
  };

  const handleClosePreview = () => {
    setPreviewState({ isOpen: false, currentIndex: 0 });
  };

  const getFileIcon = (file: UploadAttachmentFile, index: number) => {
    const isImage = file?.fileType?.startsWith('image/') ?? true;
    const hasImagePreview = isImage && (file.urlLink || file.previewData);

    const iconElement = hasImagePreview ? (
      <img
        src={file.previewData || file.urlLink}
        alt={file.title}
        style={{ height: '40px', width: '40px' }}
        className="rounded-md object-cover"
      />
    ) : (
      <div className="flex h-10 min-w-[40px] items-center justify-center rounded-md bg-gray-100">
        <UploadedDocIcon className="h-10 w-10" />
      </div>
    );

    if (enableFilePreview) {
      return (
        <div
          className="cursor-pointer outline-none"
          onClick={() => handleFileClick(index)}
          role="button"
          tabIndex={0}
          aria-label={`Preview ${file.title}`}>
          {iconElement}
        </div>
      );
    }

    return iconElement;
  };

  return (
    <div className="flex flex-col">
      <div
        {...getRootProps({
          className: twMerge(
            `${width} flex flex-col justify-center relative items-center
           border border-dashed ${height} rounded-md border-border`,
            className
          ),
          onClick: e => e.stopPropagation(),
          tabIndex: 0,
          'aria-label': 'Upload area. Drop files here or paste an image with Ctrl+V or Cmd+V.'
        })}>
        <If condition={!isUploading}>
          <div className="flex flex-col items-center gap-y-1">
            {FileUploadIcon && (
              <div>
                <FileUploadIcon onClick={openFileDialog} className="shrink-0 cursor-pointer" />
              </div>
            )}
            <div>
              <div className="mb-1 text-center text-sbase font-semibold text-primaryText">
                <span
                  id={`${id}-upload-a-file`}
                  onClick={openFileDialog}
                  className="cursor-pointer text-sbase font-semibold text-secondary">
                  Upload files
                </span>{' '}
                or drag and drop{allowPaste ? ', or paste (Ctrl+V / Cmd+V)' : ''}
              </div>
              <div className="text-center text-px13 font-semibold text-accentText">{subText}</div>
              <input {...getInputProps()} />
            </div>
          </div>
        </If>
        <If condition={isUploading}>
          <Lottie className="h-28 w-28" animationData={LoadingSpinnerAnimation} loop={true} />
        </If>
      </div>
      {showFileList && files?.length > 0 && (
        <div
          className={`customNormalScroll mt-2 flex max-h-[244px] flex-col divide-y divide-borderGray overflow-y-auto 
        rounded border border-borderGray px-4 pt-1 ${previewClassName}`}>
          {files.map((file, index) => (
            <div
              key={`file-attachment-${file.id || file.tempId || index}`}
              className="flex items-center justify-between py-2.5">
              <div className="flex items-center justify-center gap-x-4">
                {getFileIcon(file, index)}
                <div className="w-fit text-sbase font-semibold text-headingGray line-clamp-2">{file.title}</div>
              </div>
              <CircleCloseIcon
                onClick={() => onFileRemove(file.id || file.tempId || index)}
                className="shrink-0 cursor-pointer"
              />
            </div>
          ))}
        </div>
      )}
      {enableFilePreview && previewState.isOpen && files.length > 0 && (
        <AttachmentPreview files={files} selectedIndex={previewState.currentIndex} onClose={handleClosePreview} />
      )}
    </div>
  );
};

export default MultiFileUpload;
